import React, { useEffect, useState, useCallback } from "react";
import PhoneInput from "../../../common/components/PhoneInput";
import { useNavigate } from "react-router-dom";
import Navbar from "../nav_bar/Navbar";
import { FaUserCircle, FaLock, FaPhone, FaWhatsapp, FaTrashAlt } from "react-icons/fa";
import EmailPreferences from "./EmailPreferences";
import axios from "axios";
import { getUserProfile, updateUserProfile } from "../../services/userAuthService";
import "./ProfilePage.css";

const ProfilePage = () => {

 const [user, setUser] = useState(null);
 const [showResetModal, setShowResetModal] = useState(false);
 const [loading, setLoading] = useState(false);
 const [message, setMessage] = useState("");

 // Mobile number & WhatsApp state
 const [mobileNumber, setMobileNumber] = useState("");
 const [dialCode, setDialCode] = useState("+91");
 const [countryCodes, setCountryCodes] = useState([]);
 const [whatsappEnabled, setWhatsappEnabled] = useState(false);
 const [mobileError, setMobileError] = useState("");
 const [mobileSuccess, setMobileSuccess] = useState("");
 const [mobileLoading, setMobileLoading] = useState(false);

 const navigate = useNavigate();

 useEffect(() => {
  axios.get(`${process.env.REACT_APP_BASE_URL}/public/country-codes`)
   .then(res => setCountryCodes(res.data?.data || []))
   .catch(() => {});
 }, []);

 const fetchProfile = useCallback(async () => {
 try {
 const res = await getUserProfile();
 const profile = res.data;
 const whatsapp = profile?.whatsapp_enabled || profile?.whatsappEnabled || false;
 setDialCode(profile?.countryCode || "+91");
 setMobileNumber(profile?.mobileNumber || "");
 setWhatsappEnabled(whatsapp);
 } catch (err) {
 // Silently fail — profile page still renders with localStorage data
 }
 }, []);

 useEffect(() => {
 const storedUser = localStorage.getItem("user");
 const token = localStorage.getItem("token");

 if (!storedUser || !token) {
 navigate("/register", { replace: true });
 } else {
 try {
 const parsedUser = JSON.parse(storedUser);
 setUser(parsedUser);
 fetchProfile();
 } catch (error) {
 navigate("/register", { replace: true });
 }
 }
 }, [navigate, fetchProfile]);

 const handleMobileNumberUpdate = async () => {
 setMobileError("");
 setMobileSuccess("");

 if (mobileNumber) {
 const e164Regex = /^\+[1-9]\d{1,14}$/;
 if (!e164Regex.test(dialCode + mobileNumber)) {
 setMobileError("Please enter a valid mobile number");
 return;
 }
 }

 setMobileLoading(true);
 try {
 await updateUserProfile({ country_code: dialCode, mobile_number: mobileNumber || null });
 setMobileSuccess("Mobile number updated successfully");
 if (!mobileNumber) setWhatsappEnabled(false);
 } catch (err) {
 const raw = err.response?.data?.message || "";
 const msg = raw.includes("cannot be cast") || raw.includes("INTERNAL_ERROR")
 ? "Something went wrong. Please try again later."
 : raw || "Failed to update mobile number";
 setMobileError(msg);
 } finally {
 setMobileLoading(false);
 }
 };

 const handleRemoveMobileNumber = async () => {
 setMobileError("");
 setMobileSuccess("");
 setMobileLoading(true);
 try {
 await updateUserProfile({ country_code: null, mobile_number: null, whatsappEnabled: false });
 setMobileNumber("");
 setWhatsappEnabled(false);
 setMobileSuccess("Mobile number removed");
 } catch (err) {
 const raw = err.response?.data?.message || "";
 const msg = raw.includes("cannot be cast") || raw.includes("INTERNAL_ERROR")
 ? "Something went wrong. Please try again later."
 : raw || "Failed to remove mobile number";
 setMobileError(msg);
 } finally {
 setMobileLoading(false);
 }
 };

 const handleWhatsAppToggle = async (enabled) => {
 if (enabled && !mobileNumber) {
 setMobileError("Add a mobile number first to enable WhatsApp");
 return;
 }

 setMobileLoading(true);
 setMobileError("");
 setMobileSuccess("");
 try {
 await updateUserProfile({ whatsappEnabled: enabled });
 setWhatsappEnabled(enabled);
 setMobileSuccess(enabled ? "WhatsApp notifications enabled" : "WhatsApp notifications disabled");
 } catch (err) {
 const raw = err.response?.data?.message || "";
 const msg = raw.includes("cannot be cast") || raw.includes("INTERNAL_ERROR")
 ? "Something went wrong. Please try again later."
 : raw || "Failed to update WhatsApp preference";
 setMobileError(msg);
 } finally {
 setMobileLoading(false);
 }
 };

 const handleNavigateToReset = () => {
 navigate("/forgot-password", {
 state: {
 mode: "reset",
 email: user.email,
 fromProfile: true
 }
 });
 };

 const handleNavigateToReferral = () => {
 navigate("/referral");
 };

 if (!user) return null;

 const isGoogleUser = Boolean(user.googleId);

 return (
 <>
 <Navbar />

 <div className="profile-page">
 <div className="profile-container">
 <div className="profile-card">

 {/* HEADER SECTION */}
 <div className="profile-header">

 <div className="profile-avatar-wrapper">
 {user.picture ? (
 <img
 src={user.picture}
 alt={user.name}
 className="profile-avatar"
 />
 ) : (
 <div className="profile-avatar-fallback">
 {user.name?.charAt(0)?.toUpperCase() || "U"}
 </div>
 )}
 </div>

 <div className="profile-info">
 <h2 className="profile-name">
 {user.name || "User"}
 </h2>
 <p className="profile-email">{user.email}</p>

 <span className={`profile-badge ${isGoogleUser ? "google" : "email"}`}>
 {isGoogleUser ? "Google Account" : "Email Account"}
 </span>
 </div>

 </div>

 <div className="divider" />

 {/* SECURITY SECTION */}
 <div className="profile-section">

 <div className="section-header">
 <h3>Security</h3>
 </div>

 <button
 className="btn-outline"
 disabled={isGoogleUser}
 onClick={() => {
 if (!isGoogleUser) {
 handleNavigateToReset();
 }
 }}
 >
 <FaLock /> Reset Password
 </button>

 {isGoogleUser && (
 <p className="hint">
 Password is managed securely via Google.
 </p>
 )}
 </div>

 <div className="divider" />

 {/* EMAIL PREFERENCES SECTION */}
 <div className="profile-section">
 <EmailPreferences />
 </div>

 <div className="divider" />

 {/* MOBILE NUMBER & WHATSAPP SECTION */}
 <div className="profile-section">
 <div className="section-header">
 <h3><FaPhone style={{ marginRight: 6 }} /> Mobile & WhatsApp</h3>
 </div>

 <div className="mobile-input-group">
 <PhoneInput
 countryCodes={countryCodes}
 dialCode={dialCode}
 onDialCodeChange={(val) => { setDialCode(val); setMobileError(""); setMobileSuccess(""); }}
 mobileNumber={mobileNumber}
 onMobileNumberChange={(val) => { setMobileNumber(val); setMobileError(""); setMobileSuccess(""); }}
 placeholder="9876543210"
 />
 <button
 className="btn-outline mobile-save-btn"
 onClick={handleMobileNumberUpdate}
 disabled={mobileLoading}
 >
 {mobileLoading ? "Saving..." : "Save"}
 </button>
 </div>
 {mobileNumber && (
 <button
 className="btn-outline mobile-remove-btn"
 onClick={handleRemoveMobileNumber}
 disabled={mobileLoading}
 >
 Remove Mobile Number
 </button>
 )}

 {mobileError && <p className="profile-error">{mobileError}</p>}
 {mobileSuccess && <p className="profile-success">{mobileSuccess}</p>}

 <div className="whatsapp-toggle">
 <label className="toggle-label">
 <input
 type="checkbox"
 checked={whatsappEnabled}
 disabled={!mobileNumber || mobileLoading}
 onChange={(e) => handleWhatsAppToggle(e.target.checked)}
 />
 <FaWhatsapp style={{ color: "#25D366", marginLeft: 6, marginRight: 4 }} />
 Enable WhatsApp notifications
 </label>
 {!mobileNumber && (
 <small className="hint">Add a mobile number to enable WhatsApp</small>
 )}
 </div>
 </div>

 <div className="divider" />

 {/* REFERRAL SECTION */}
 <div className="profile-section">

 <div className="section-header">
 <h3>Referral Program</h3>
 <span className="section-tag">Earn Rewards</span>
 </div>

 <p className="hint">
 Invite friends and earn free speaking sessions.
 </p>

 <button
 className="btn-primary"
 onClick={handleNavigateToReferral}
 >
 View Referral Dashboard
 </button>
 </div>

 <div className="divider" />

 {/* DANGER ZONE */}
 <div className="profile-section">
 <div className="section-header">
 <h3>Danger Zone</h3>
 </div>
 <p className="hint">Permanently delete your account and all associated data.</p>
 <button
 className="btn-danger-outline"
 onClick={() => navigate("/account/delete")}
 >
 <FaTrashAlt /> Delete Account
 </button>
 </div>

 </div>
 </div>
 </div>
 </>
);
};

export default ProfilePage;
